/**
 * Reliable Monte Carlo Engine - Integrated Mathematical Foundation
 * 
 * This integrates all advanced mathematical capabilities from deprecated engines:
 * - Fat-tail modeling from EnhancedMonteCarloEngine
 * - Income/capital split optimization from ImprovedMonteCarloEngine  
 * - Entity-aware taxation from EntityAwareMonteCarloEngine
 * - Enhanced return generation with realistic correlations
 * - Black swan event modeling and regime switching
 * - Advanced statistical validation and convergence testing
 */

import { ADTScenario, SimulationResult } from '@/types/adt';
import { TimelineEvent } from '@/types/stressTesting';
import { MathematicalCore } from './MathematicalCore';
import { logger } from '@/lib/utils/logger';
import { SimulationParameterSet } from '@/hooks/useSimulationParameters';
import { RandomGenerator } from './RandomGenerator';
import { CorrelationEngine, CorrelationSettings } from './CorrelationEngine';
import { MathUtils } from './utils/MathUtils';
import { PortfolioSimulator } from './PortfolioSimulator';
import { AustralianPensionCalculator } from '../australianCompliance/AustralianPensionCalculator';
import { GARCHPathGenerator, GARCHParameters } from './GARCHPathGenerator';
import { SuperannuationCaps2025 } from '../superannuation/SuperannuationCaps2025';
import { DebtProjectionEngine } from './DebtProjectionEngine';
import { SuperannuationContributionEngine } from './SuperannuationContributionEngine';

// Enhanced mathematical settings integrating all capabilities
export interface IntegratedMathematicalSettings {
  // Core Monte Carlo settings
  numberOfSimulations: number;
  confidenceLevel: number;
  randomSeed?: number;
  
  // Advanced mathematical features
  enableFatTails: boolean;
  enableRegimeSwitching: boolean;
  enableBlackSwanEvents: boolean;
  degreesOfFreedom: number;
  blackSwanProbability: number;
  blackSwanImpact: number;
  
  // Australian financial integration
  enableCentrelinkIntegration: boolean;
  enableSuperannuationOptimization: boolean;
  enablePropertyIntegration: boolean;
  enableHELPDebtCalculation: boolean;
  enableEntityTaxOptimization: boolean;
  
  // Return generation enhancements
  enableIncomeCapitalSplit: boolean;
  enableRealisticCorrelations: boolean;
  enableSeasonalityEffects: boolean;
  enableFrankingCredits: boolean;
  
  // GARCH volatility modeling
  enableGARCHVolatility: boolean;
  garchParameters?: {
    growth: GARCHParameters;
    defensive: GARCHParameters;
  };
  
  // Path visualization settings
  pathVisualization?: {
    enabled: boolean;
    maxPathsToStore: number;
    samplingStrategy: 'random' | 'stratified' | 'extreme';
  };
}

// Legacy interface for backward compatibility
export interface MonteCarloSettings {
  numberOfSimulations: number;
  confidenceLevel: number;
  randomSeed?: number;
}

// Enhanced simulation metrics with advanced mathematical validation
export interface IntegratedSimulationMetrics {
  convergenceAchieved: boolean;
  finalSimulations: number;
  confidenceLevel: number;
  standardError: number;
  outlierCount: number;
  
  // Mathematical validation metrics
  distributionHealthScore: number;
  correlationStability: number;
  fatTailDetected: boolean;
  regimeChangesDetected: number;
  blackSwanEventsOccurred: number;
  
  // Australian financial metrics
  agePensionEligibilityRate: number;
  superannuationTaxEfficiency: number;
  centrelinkComplianceScore: number;
  
  statisticalHealth: 'excellent' | 'good' | 'acceptable' | 'poor';
}

export interface TimelinePoint {
  year: number;
  age: number;
  portfolioValue: number;
  contributions: number;
  withdrawals: number;
  inflation: number;
  realPortfolioValue: number;
  phase: 'accumulation' | 'retirement';
  
  // Enhanced return breakdown
  totalReturn?: number;
  priceReturn?: number;
  incomeReturn?: number;
  
  // Fee and tax breakdown
  platformFees?: number;
  taxes?: number;
  netReturn?: number;
  
  // Income breakdown
  employmentIncome?: number;
  portfolioWithdrawal?: number;
  pensionIncome?: number;
  otherIncome?: number;
  totalIncome?: number;
  
  // Expense breakdown
  livingExpenses?: number;
  housingCosts?: number;
  totalExpenses?: number;
  
  // Asset allocation
  growthAllocation?: number;
  defensiveAllocation?: number;
}

export class ReliableMonteCarloEngine {
  private settings: IntegratedMathematicalSettings;
  private parameters?: SimulationParameterSet;
  private randomSeed: number;
  private currentScenario?: ADTScenario;
  
  // Advanced mathematical components
  private randomGenerator: RandomGenerator;
  private correlationEngine: CorrelationEngine;
  private garchGenerators?: {
    growth: GARCHPathGenerator;
    defensive: GARCHPathGenerator;
  };

  /**
   * Validate scenario completeness and detect missing fields
   */
  private validateScenarioCompleteness(scenario: ADTScenario): {
    isComplete: boolean;
    missingFields: string[];
    warnings: string[];
  } {
    const missing: string[] = [];
    const warnings: string[] = [];

    // Check critical fields
    if (!scenario.basicInformation?.currentAge) missing.push('basicInformation.currentAge');
    if (!scenario.basicInformation?.retirementAge) missing.push('basicInformation.retirementAge');
    if (!scenario.assets?.superBalance && scenario.assets?.superBalance !== 0) missing.push('assets.superBalance');
    if (!scenario.strategy?.allocationToGrowth && scenario.strategy?.allocationToGrowth !== 0) missing.push('strategy.allocationToGrowth');

    // Check optional but recommended fields
    if (scenario.basicInformation?.hasPartner && scenario.basicInformation?.jointFinancialPlanning) {
      if (!scenario.partnerAssets) {
        warnings.push('Partner assets missing for joint financial planning');
      }
      if (!scenario.partnerIncome) {
        warnings.push('Partner income missing for joint financial planning');
      }
      if (!scenario.basicInformation.partnerRetirementAge) {
        warnings.push('Partner retirement age missing for joint financial planning');
      }
    }

    if (scenario.propertyPortfolio?.enabled) {
      if (!scenario.propertyPortfolio.properties || scenario.propertyPortfolio.properties.length === 0) {
        warnings.push('Property portfolio enabled but no properties defined');
      }
      if (!scenario.propertyPortfolio.totalValue) {
        warnings.push('Property portfolio enabled but totalValue is missing');
      }
    }

    if (scenario.enhancedDebtConfiguration?.enabled) {
      if (!scenario.enhancedDebtConfiguration.accounts || scenario.enhancedDebtConfiguration.accounts.length === 0) {
        warnings.push('Enhanced debt configuration enabled but no debt accounts defined');
      }
      if (!scenario.enhancedDebtConfiguration.totalMonthlyRepayments) {
        warnings.push('Enhanced debt configuration enabled but totalMonthlyRepayments is missing');
      }
    }

    if (scenario.incomeGrowthSettings?.enabled) {
      if (scenario.incomeGrowthSettings.primaryIncomeGrowthRate === undefined) {
        warnings.push('Income growth settings enabled but primaryIncomeGrowthRate is missing');
      }
    }

    if (scenario.cashflowModeling?.enabled) {
      if (scenario.cashflowModeling.monthlyNetCashflow === undefined) {
        warnings.push('Cashflow modeling enabled but monthlyNetCashflow is missing');
      }
    }

    // Check contribution history for accurate carry-forward calculations
    const unusedCaps = scenario.strategy?.criticalDataFoundation?.unusedConcessionalCapHistory || [];
    if (unusedCaps.length === 0 && scenario.superannuationContributions?.voluntaryConcessional) {
      warnings.push('No contribution history found. Carry-forward calculations will use conservative 50% assumption.');
    }

    return {
      isComplete: missing.length === 0,
      missingFields: missing,
      warnings
    };
  }

  constructor(
    settings?: Partial<IntegratedMathematicalSettings>, 
    parameters?: SimulationParameterSet
  ) {
    // Integrated mathematical settings with all advanced capabilities
    this.settings = {
      // Core settings
      numberOfSimulations: settings?.numberOfSimulations || 1000,
      confidenceLevel: settings?.confidenceLevel || 95,
      randomSeed: settings?.randomSeed || Date.now(),
      
      // Advanced mathematical features (preserved from EnhancedMonteCarloEngine)
      enableFatTails: settings?.enableFatTails ?? true,
      enableRegimeSwitching: settings?.enableRegimeSwitching ?? true,
      enableBlackSwanEvents: settings?.enableBlackSwanEvents ?? false,
      degreesOfFreedom: settings?.degreesOfFreedom || 5,
      blackSwanProbability: settings?.blackSwanProbability || 0.01,
      blackSwanImpact: settings?.blackSwanImpact || 0.30,
      
      // Australian financial integration
      enableCentrelinkIntegration: settings?.enableCentrelinkIntegration ?? true,
      enableSuperannuationOptimization: settings?.enableSuperannuationOptimization ?? true,
      enablePropertyIntegration: settings?.enablePropertyIntegration ?? true,
      enableHELPDebtCalculation: settings?.enableHELPDebtCalculation ?? true,
      enableEntityTaxOptimization: settings?.enableEntityTaxOptimization ?? true,
      
      // Enhanced return generation (preserved from ImprovedMonteCarloEngine)
      enableIncomeCapitalSplit: settings?.enableIncomeCapitalSplit ?? true,
      enableRealisticCorrelations: settings?.enableRealisticCorrelations ?? true,
      enableSeasonalityEffects: settings?.enableSeasonalityEffects ?? false,
      enableFrankingCredits: settings?.enableFrankingCredits ?? true,
      
      // GARCH volatility modeling
      enableGARCHVolatility: settings?.enableGARCHVolatility ?? false,
      garchParameters: settings?.garchParameters,
      
      // Path visualization settings
      pathVisualization: {
        enabled: settings?.pathVisualization?.enabled ?? false, // Disabled by default to avoid memory overhead
        maxPathsToStore: settings?.pathVisualization?.maxPathsToStore ?? 100,
        samplingStrategy: settings?.pathVisualization?.samplingStrategy ?? 'stratified'
      }
    };
    
    this.parameters = parameters;
    this.randomSeed = this.settings.randomSeed;
    
    // Initialize advanced mathematical components
    this.randomGenerator = new RandomGenerator(this.randomSeed);
    this.correlationEngine = new CorrelationEngine(this.randomSeed);
    
    // Initialize GARCH generators if enabled
    if (this.settings.enableGARCHVolatility && this.settings.garchParameters) {
      try {
        this.garchGenerators = {
          growth: new GARCHPathGenerator(this.settings.garchParameters.growth),
          defensive: new GARCHPathGenerator(this.settings.garchParameters.defensive)
        };
        logger.info('✅ GARCH volatility modeling enabled');
      } catch (error) {
        logger.warn('⚠️ GARCH initialization failed, falling back to GBM:', error);
        this.settings.enableGARCHVolatility = false;
      }
    }
    
    logger.info('🔬 Integrated Monte Carlo Engine initialized with advanced mathematical capabilities');
  }

  /**
   * Run Monte Carlo simulation - single, reliable method
   * Enhanced with integrated mathematical capabilities
   */
  async runSimulation(
    scenario: ADTScenario, 
    stressEvents?: TimelineEvent[]
  ): Promise<SimulationResult> {
    logger.info('🎲 Running Integrated Monte Carlo Simulation');
    
    // Store scenario for access in trial methods
    this.currentScenario = scenario;
    
    // Validate scenario completeness
    const validation = this.validateScenarioCompleteness(scenario);
    if (!validation.isComplete) {
      logger.warn('⚠️ Missing critical fields:', validation.missingFields);
    }
    if (validation.warnings.length > 0) {
      logger.warn('⚠️ Scenario warnings:', validation.warnings);
    }
    
    // 1. Validate inputs thoroughly
    this.validateScenario(scenario);
    
    // 2. Extract core parameters
    const params = this.extractParameters(scenario);
    
    // 3. Run Monte Carlo trials with mathematical enhancements
    const trials = this.runEnhancedMonteCarloTrials(params, stressEvents);
    
    // 4. Calculate enhanced statistics
    const statistics = this.calculateEnhancedStatistics(trials);
    
    // 4.5. Run statistical validation
    let statisticalValidation;
    try {
      const trialValues = trials.map(t => t.finalValue);
      const ksTest = this.runKolmogorovSmirnovTest(trialValues);
      const adTest = this.runAndersonDarlingTest(trialValues);
      
      statisticalValidation = {
        kolmogorovSmirnov: ksTest,
        andersonDarling: adTest,
        samplingQuality: this.determineSamplingQuality(trials.length, statistics),
        warnings: this.generateValidationWarnings(ksTest, adTest, trials.length)
      };
      
      logger.info('✅ Statistical validation complete:', {
        ksTest: `${ksTest.passed ? '✓' : '✗'} (p=${ksTest.pValue.toFixed(3)})`,
        adTest: `${adTest.passed ? '✓' : '✗'} (p=${adTest.pValue.toFixed(3)})`,
        quality: statisticalValidation.samplingQuality
      });
    } catch (error) {
      logger.error('Statistical validation failed:', error);
      statisticalValidation = {
        kolmogorovSmirnov: { statistic: 0, pValue: 0, passed: false },
        andersonDarling: { statistic: 0, pValue: 0, passed: false },
        samplingQuality: 'adequate' as const,
        warnings: ['Statistical validation could not be completed']
      };
    }
    
    // 4.6. Aggregate superannuation tax data
    const superannuationTaxSummary = this.aggregateSuperannuationTaxData(
      trials,
      params.currentAge,
      params.retirementAge || 67
    );
    
    // 4.7. Extract trial paths for visualization
    const successThreshold = params.targetAnnualIncome || 0; // Use target income as success threshold
    const trialPaths = trials
      .filter(trial => trial.path && trial.path.length > 0)
      .map(trial => ({
        trialIndex: trial.trialIndex,
        outcome: (trial.finalValue >= successThreshold ? 'success' : 'failure') as 'success' | 'failure',
        finalValue: trial.finalValue,
        breachedFloor: trial.breachedFloor || false,
        breachedAt: trial.breachedAt,
        path: trial.path!
      }));
    
    if (trialPaths.length > 0) {
      logger.info(`📊 Captured ${trialPaths.length} trial paths for visualization`);
    }
    
    // 5. Generate enhanced timeline data
    const timelinePercentiles = this.calculateTimelinePercentiles(trials, params);
    const timelineData = this.generateTimelineData(params, statistics.medianTrial);
    const chartData = this.generateChartData(timelineData);
    
    // 6. Build comprehensive result
    const result: SimulationResult = {
      // Core metrics
      successRate: statistics.successRate,
      shortfallProbability: 100 - statistics.successRate,
      medianOutcome: statistics.percentiles.p50,
      averageOutcome: statistics.average,
      bestCaseOutcome: statistics.percentiles.p90,
      worstCaseOutcome: statistics.percentiles.p10,
      
      // Enhanced percentiles
      percentiles: {
        year: new Date().getFullYear() + (params.retirementAge - params.currentAge),
        p10: statistics.percentiles.p10,
        p25: statistics.percentiles.p25,
        p50: statistics.percentiles.p50,
        p75: statistics.percentiles.p75,
        p90: statistics.percentiles.p90
      },
      
      // Timeline data
      timelinePercentiles,
      platformFeesPercentiles: this.calculatePlatformFeesPercentiles(trials, params, scenario),
      incomePercentiles: this.calculateIncomePercentiles(trials, params, scenario),
      expensePercentiles: this.calculateExpensePercentiles(trials, params, scenario),
      chartData,
      trialOutcomes: trials.map(t => t.finalValue),
      
      // Timeline data for visualizations
      timelineData: timelineData.map(point => ({
        year: point.year,
        age: point.age,
        portfolioValue: point.portfolioValue,
        realPortfolioValue: point.realPortfolioValue,
        contributions: point.contributions,
        withdrawals: point.withdrawals,
        phase: point.phase,
        currentAge: point.age,
        value: point.portfolioValue,
        portfolioWithdrawal: point.portfolioWithdrawal || (point.phase === 'retirement' ? point.withdrawals : 0),
        employmentIncome: point.employmentIncome || (point.phase === 'accumulation' ? params.primaryIncome : 0),
        pensionIncome: point.pensionIncome || 0,
        totalIncome: point.totalIncome || (point.contributions + point.withdrawals),
        totalExpenses: point.totalExpenses || (point.phase === 'retirement' ? point.withdrawals : 0),
        totalReturn: point.totalReturn || 0,
        priceReturn: point.priceReturn || 0,
        incomeReturn: point.incomeReturn || 0,
        netReturn: point.netReturn || 0,
      })),
      
      // Portfolio metrics
      finalPortfolioValue: statistics.medianTrial.finalValue,
      totalContributions: params.annualContributions * params.accumYears,
      totalWithdrawals: 0,
      netInvestmentGain: statistics.medianTrial.finalValue - (params.initialPortfolio + params.annualContributions * params.accumYears),
      compoundAnnualGrowthRate: params.expectedReturn,
      
      // Risk metrics
      maxDrawdownPeriod: { start: new Date(), end: new Date(), duration: statistics.maxDrawdownPeriod || 0 },
      probabilityOfSuccess: statistics.successRate,
      shortfall: 100 - statistics.successRate,
      
      // Scenario sample for visualization  
      scenarios: this.generateScenarioSample(trials),
      timeSeriesData: timelineData,
      
      // Analysis and recommendations
      summary: this.generateEnhancedAnalysis(statistics, params),
      detailedAnalysis: this.generateEnhancedAnalysis(statistics, params),
      recommendedStrategy: this.generateRecommendation(statistics, params),
      
      // Legacy compatibility
      finalValue: statistics.medianTrial.finalValue,
      projectedIncome: params.primaryIncome,
      
      // Enhanced Monte Carlo metadata
      monteCarloMetadata: {
        isMonteCarlo: this.settings.numberOfSimulations > 1,
        actualSimulationCount: trials.length,
        targetSimulationCount: this.settings.numberOfSimulations,
        confidenceLevel: this.settings.confidenceLevel,
        convergenceStatus: statistics.convergenceAchieved,
        simulationMode: 'full' as const,
        engineType: 'IntegratedMathematicalEngine',
        sr117Compliant: true,
        trialPaths: trialPaths.length > 0 ? trialPaths : undefined
      },
      
      // Metadata with statistical validation and feature flags
      metadata: {
        simulationId: `sim_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: Date.now(),
        buildVersion: '3.0',
        statisticalValidation,
        enableGARCH: this.settings.enableGARCHVolatility || false
      },
      
      // Superannuation tax summary
      superannuationTaxSummary
    };
    
    logger.info('✅ Integrated Monte Carlo simulation completed successfully');
    return result;
  }

  /**
   * Enhanced Monte Carlo trials with mathematical sophistication
   */
  private runEnhancedMonteCarloTrials(params: any, stressEvents?: TimelineEvent[]) {
    const trials = [];
    const pathVisualizationEnabled = this.settings.pathVisualization?.enabled ?? false;
    const maxPathsToStore = this.settings.pathVisualization?.maxPathsToStore ?? 100;
    const samplingStrategy = this.settings.pathVisualization?.samplingStrategy ?? 'stratified';
    
    // Determine which trials to capture paths for
    const trialsToCapture = this.selectTrialsForPathCapture(
      this.settings.numberOfSimulations,
      maxPathsToStore,
      samplingStrategy
    );
    
    for (let i = 0; i < this.settings.numberOfSimulations; i++) {
      // Generate enhanced returns with advanced mathematical models
      const returns = this.generateAdvancedReturns(
        params.accumYears + params.retireYears, 
        params, 
        i
      );
      
      // Run single trial with enhanced calculations
      const capturePath = pathVisualizationEnabled && trialsToCapture.has(i);
      const trial = this.runEnhancedTrial(returns, params, stressEvents, i, capturePath);
      trials.push(trial);
    }
    
    return trials;
  }

  /**
   * Select which trials to capture paths for based on sampling strategy
   */
  private selectTrialsForPathCapture(
    totalTrials: number,
    maxPaths: number,
    strategy: 'random' | 'stratified' | 'extreme'
  ): Set<number> {
    const selected = new Set<number>();
    
    if (maxPaths >= totalTrials) {
      // Capture all trials
      for (let i = 0; i < totalTrials; i++) {
        selected.add(i);
      }
      return selected;
    }
    
    switch (strategy) {
      case 'stratified':
        // Sample evenly across the trial range
        const step = totalTrials / maxPaths;
        for (let i = 0; i < maxPaths; i++) {
          selected.add(Math.floor(i * step));
        }
        break;
        
      case 'extreme':
        // Capture first and last trials (useful for percentile extremes)
        const extremePaths = Math.floor(maxPaths / 3);
        for (let i = 0; i < extremePaths; i++) {
          selected.add(i);
          selected.add(totalTrials - 1 - i);
        }
        // Fill remaining with stratified sampling
        const remaining = maxPaths - (extremePaths * 2);
        const midStep = totalTrials / remaining;
        for (let i = 0; i < remaining; i++) {
          selected.add(Math.floor(extremePaths + i * midStep));
        }
        break;
        
      case 'random':
      default:
        // Random sampling
        while (selected.size < maxPaths) {
          selected.add(Math.floor(Math.random() * totalTrials));
        }
        break;
    }
    
    return selected;
  }

  /**
   * Generate mathematically sophisticated returns
   * Integrating fat-tail, regime switching, GARCH, and correlation modeling
   */
  private generateAdvancedReturns(years: number, params: any, seed: number) {
    // Use GARCH if enabled
    if (this.settings.enableGARCHVolatility && this.garchGenerators) {
      return this.generateGARCHReturns(years, params);
    }
    
    // Otherwise use correlation engine with dynamic regime detection
    const correlationSettings = CorrelationEngine.getDefaultSettings();
    correlationSettings.marketRegimeSettings.enabled = this.settings.enableRegimeSwitching;
    
    // Initialize correlation engine to get dynamic regime
    const tempCorrelationEngine = new CorrelationEngine(seed);
    
    const returns = [];
    let randomState = seed + this.randomSeed;
    let currentRegime = tempCorrelationEngine.getRegimeDetector().getCurrentRegimeSync().current;
    let regimeYearsRemaining = 0;
    
    for (let i = 0; i < years; i++) {
      // Regime switching logic
      if (this.settings.enableRegimeSwitching) {
        if (regimeYearsRemaining <= 0) {
          const regimeRandom = this.seededRandom(randomState++);
          if (regimeRandom < 0.05) {
            currentRegime = 'crisis';
            regimeYearsRemaining = 2 + Math.floor(this.seededRandom(randomState++) * 3);
          } else if (regimeRandom < 0.15) {
            currentRegime = 'high_volatility';
            regimeYearsRemaining = 3 + Math.floor(this.seededRandom(randomState++) * 5);
          } else {
            currentRegime = 'normal';
            regimeYearsRemaining = 5 + Math.floor(this.seededRandom(randomState++) * 10);
          }
        }
        regimeYearsRemaining--;
      }
      
      // Generate correlated random variables
      const u1 = this.seededRandom(randomState++);
      const u2 = this.seededRandom(randomState++);
      
      // Box-Muller transformation for normal distribution
      const z1 = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
      const z2 = Math.sqrt(-2 * Math.log(u1)) * Math.sin(2 * Math.PI * u2);
      
      // Fat-tail modeling using t-distribution
      let growthReturn, defensiveReturn;
      if (this.settings.enableFatTails) {
        // Convert normal to t-distribution
        const chi2Random = this.generateChiSquared(this.settings.degreesOfFreedom, randomState);
        randomState += this.settings.degreesOfFreedom;
        
        const tScale = Math.sqrt(this.settings.degreesOfFreedom / chi2Random);
        growthReturn = z1 * tScale;
        defensiveReturn = z2 * tScale;
      } else {
        growthReturn = z1;
        defensiveReturn = z2;
      }
      
      // Apply regime-dependent correlation and volatility
      const { correlation, growthVol, defensiveVol } = this.getRegimeParameters(currentRegime, params);
      
      // Apply correlation using Cholesky decomposition
      const correlatedDefensive = correlation * growthReturn + 
        Math.sqrt(1 - correlation * correlation) * defensiveReturn;
      
      // Scale by expected returns and volatilities
      const finalGrowthReturn = params.expectedReturn + growthReturn * growthVol;
      const finalDefensiveReturn = params.defensiveReturn + correlatedDefensive * defensiveVol;
      
      // Income/capital split for enhanced return decomposition
      let incomeComponent, capitalComponent;
      if (this.settings.enableIncomeCapitalSplit) {
        incomeComponent = this.calculateIncomeReturn(finalGrowthReturn, params);
        capitalComponent = finalGrowthReturn - incomeComponent;
      } else {
        incomeComponent = finalGrowthReturn * 0.3; // Default split
        capitalComponent = finalGrowthReturn * 0.7;
      }
      
      // Black swan events
      let blackSwanMultiplier = 1;
      if (this.settings.enableBlackSwanEvents) {
        if (this.seededRandom(randomState++) < this.settings.blackSwanProbability) {
          blackSwanMultiplier = 1 - this.settings.blackSwanImpact;
        }
      }
      
      returns.push({
        growth: finalGrowthReturn * blackSwanMultiplier,
        defensive: finalDefensiveReturn * blackSwanMultiplier,
        income: incomeComponent * blackSwanMultiplier,
        capital: capitalComponent * blackSwanMultiplier,
        regime: currentRegime,
        correlation: correlation
      });
    }
    
    return returns;
  }

  /**
   * Generate returns using GARCH(1,1) volatility modeling
   */
  private generateGARCHReturns(years: number, params: any) {
    if (!this.garchGenerators) {
      logger.warn('GARCH generators not initialized, falling back to standard returns');
      return this.generateAdvancedReturns(years, params, 0);
    }
    
    try {
      // Generate GARCH paths for growth and defensive assets
      const growthPath = this.garchGenerators.growth.generatePath({
        periods: years,
        includeJumps: this.settings.enableBlackSwanEvents,
        jumpProbability: this.settings.blackSwanProbability,
        jumpMagnitude: this.settings.blackSwanImpact
      });
      
      const defensivePath = this.garchGenerators.defensive.generatePath({
        periods: years,
        includeJumps: false // Defensive assets typically don't have jumps
      });
      
      // Transform GARCH returns into the format expected by the simulation
      const returns = [];
      for (let i = 0; i < years; i++) {
        const growthReturn = growthPath.returns[i];
        const defensiveReturn = defensivePath.returns[i];
        
        // Income/capital split
        let incomeComponent, capitalComponent;
        if (this.settings.enableIncomeCapitalSplit) {
          incomeComponent = this.calculateIncomeReturn(growthReturn, params);
          capitalComponent = growthReturn - incomeComponent;
        } else {
          incomeComponent = growthReturn * 0.3;
          capitalComponent = growthReturn * 0.7;
        }
        
        // Determine regime based on volatility level
        const growthVol = growthPath.volatilities[i];
        const avgVol = this.garchGenerators.growth.getTheoreticalMoments().unconditionalVolatility;
        let regime = 'normal';
        if (growthVol > avgVol * 1.5) {
          regime = 'crisis';
        } else if (growthVol > avgVol * 1.2) {
          regime = 'volatile';
        }
        
        returns.push({
          growth: growthReturn * 100, // Convert to percentage
          defensive: defensiveReturn * 100,
          income: incomeComponent * 100,
          capital: capitalComponent * 100,
          regime,
          correlation: 0.3, // GARCH handles correlation implicitly through time-varying covariance
          volatility: growthVol
        });
      }
      
      return returns;
    } catch (error) {
      logger.error('GARCH return generation failed:', error);
      // Fallback to standard returns
      return this.generateAdvancedReturns(years, params, 0);
    }
  }

  /**
   * Get regime-dependent parameters
   */
  private getRegimeParameters(regime: string, params: any) {
    switch (regime) {
      case 'crisis':
        return {
          correlation: 0.8, // Higher correlation in crisis
          growthVol: params.volatility * 2.0,
          defensiveVol: params.defensiveVolatility * 1.5
        };
      case 'volatile':
        return {
          correlation: 0.4,
          growthVol: params.volatility * 1.3,
          defensiveVol: params.defensiveVolatility * 1.1
        };
      default: // normal
        return {
          correlation: 0.2, // Low correlation in normal times
          growthVol: params.volatility,
          defensiveVol: params.defensiveVolatility
        };
    }
  }

  /**
   * Calculate income component of return with franking credits
   */
  private calculateIncomeReturn(totalReturn: number, params: any): number {
    // Base dividend yield
    let incomeReturn = totalReturn * 0.35; // Typical dividend component
    
    // Add franking credits if enabled
    if (this.settings.enableFrankingCredits) {
      const frankingBenefit = incomeReturn * 0.30 * 0.70; // 30% tax rate, 70% franked
      incomeReturn += frankingBenefit;
    }
    
    return incomeReturn;
  }

  /**
   * Generate Chi-squared random variable for t-distribution
   */
  private generateChiSquared(degreesOfFreedom: number, seed: number): number {
    let sum = 0;
    for (let i = 0; i < degreesOfFreedom; i++) {
      const u = this.seededRandom(seed + i);
      const normal = Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * this.seededRandom(seed + i + 100));
      sum += normal * normal;
    }
    return sum;
  }

  /**
   * Enhanced single trial with Australian financial integration
   */
  private runEnhancedTrial(
    returns: any[], 
    params: any, 
    stressEvents?: TimelineEvent[], 
    trialIndex = 0,
    capturePath = false
  ) {
    let portfolioValue = params.initialPortfolio;
    const timeline = [];
    const path = capturePath ? [] : undefined; // Only allocate if capturing
    let maxDrawdown = 0;
    let peakValue = portfolioValue;
    
    // Enhanced tracking
    let totalTaxesPaid = 0;
    let totalPensionReceived = 0;
    let totalSuperContributions = 0;
    
    // Track current income for Australian tax calculations
    let currentIncome = params.primaryIncome;
    
    // Task 1.3: Track partner income separately
    let currentPartnerIncome = params.partnerIncome || 0;
    
    // Task 1.2: Initialize debt tracking
    let debtBalances: Record<string, number> | undefined;
    if (this.currentScenario?.enhancedDebtConfiguration?.enabled) {
      debtBalances = {};
      for (const account of this.currentScenario.enhancedDebtConfiguration.accounts) {
        if (account.enabled) {
          debtBalances[account.id] = account.balance;
        }
      }
    }
    
    // Accumulation phase with enhanced calculations
    for (let year = 0; year < params.accumYears; year++) {
      const age = params.currentAge + year;
      const yearReturns = returns[year];
      
      // Task 2.2: Apply differentiated income growth rates
      if (this.currentScenario?.incomeGrowthSettings?.enabled) {
        // Apply primary income growth rate
        const primaryGrowthRate = this.currentScenario.incomeGrowthSettings.primaryIncomeGrowthRate || 3.5;
        currentIncome *= (1 + primaryGrowthRate / 100);
        
        // Apply partner income growth rate (only if partner hasn't retired)
        const partnerRetirementAge = params.partnerRetirementAge || params.retirementAge;
        if (age < partnerRetirementAge) {
          const partnerGrowthRate = this.currentScenario.incomeGrowthSettings.partnerIncomeGrowthRate || 3.5;
          currentPartnerIncome *= (1 + partnerGrowthRate / 100);
        }
        
        logger.info(`📈 Year ${year}: Primary income growth ${primaryGrowthRate}%, Partner growth ${this.currentScenario.incomeGrowthSettings.partnerIncomeGrowthRate}%`);
      } else {
        // Fallback to uniform wage growth
        currentIncome *= (1 + params.wageGrowthRate / 100);
        
        const partnerRetirementAge = params.partnerRetirementAge || params.retirementAge;
        if (age < partnerRetirementAge) {
          currentPartnerIncome *= (1 + params.wageGrowthRate / 100);
        }
      }
      
      // Task 2.3: Apply cashflow modeling (monthly variations to annual)
      let cashflowAdjustment = 0;
      if (this.currentScenario?.cashflowModeling?.enabled) {
        const monthlyNetCashflow = this.currentScenario.cashflowModeling.monthlyNetCashflow || 0;
        const annualCashflowGrowth = this.currentScenario.cashflowModeling.annualCashflowGrowth || 0;
        
        // Aggregate monthly cashflows with intra-year growth
        for (let month = 0; month < 12; month++) {
          const monthlyGrowth = Math.pow(1 + annualCashflowGrowth / 100, month / 12);
          cashflowAdjustment += monthlyNetCashflow * monthlyGrowth;
        }
        
        logger.info(`💰 Year ${year}: Cashflow adjustment from monthly modeling: $${cashflowAdjustment.toFixed(2)}`);
      }
      
      // Task 1.1: Add rental income with inflation adjustment
      const inflationAdjustedRentalIncome = params.rentalIncome 
        ? params.rentalIncome * Math.pow(1 + params.inflationRate / 100, year)
        : 0;
      
      // Task 2.1: Enhanced contribution calculations with SuperannuationContributionEngine
      let annualContributions = params.annualContributions;
      let contributionsTax = 0;
      let division293Tax = 0;
      
      if (this.settings.enableSuperannuationOptimization && this.currentScenario) {
        const contributionResult = SuperannuationContributionEngine.calculateAnnualContributions(
          this.currentScenario,
          year,
          age,
          currentIncome,
          portfolioValue
        );
        
        annualContributions = contributionResult.effectiveContribution; // After tax
        contributionsTax = contributionResult.taxOnContributions;
        division293Tax = contributionResult.division293Tax;
        totalSuperContributions += contributionResult.voluntaryConcessional;
        
        if (contributionResult.warnings.length > 0 && year === 0) {
          logger.warn('⚠️ Superannuation contribution warnings:', contributionResult.warnings);
        }
      }
      
      // Apply contributions
      portfolioValue += annualContributions;
      
      // Apply returns with regime awareness
      const totalReturn = (yearReturns.growth * params.growthAllocation + 
                          yearReturns.defensive * (1 - params.growthAllocation)) / 100;
      portfolioValue *= (1 + totalReturn);
      
      // Apply platform fees using comprehensive calculation
      const platformFee = PortfolioSimulator.calculatePlatformFees(portfolioValue, this.currentScenario!);
      portfolioValue -= platformFee;
      
      // Task 1.2: Process debt repayments
      let debtRepayment = 0;
      let debtInterest = 0;
      let totalDebtBalance = 0;
      if (this.currentScenario?.enhancedDebtConfiguration?.enabled && debtBalances) {
        try {
          const debtConfig = this.currentScenario.enhancedDebtConfiguration;
          
          // Handle case where repaymentStrategy might be undefined or a string
          const repaymentStrategy = typeof debtConfig.repaymentStrategy === 'object' 
            ? debtConfig.repaymentStrategy 
            : {
                type: 'avalanche' as const,
                extraRepayment: 0,
                priorityOrder: [],
                description: 'Default avalanche strategy'
              };
          
          const debtConfigWithStrategy = {
            ...debtConfig,
            repaymentStrategy,
            riskAssessment: debtConfig.riskAssessment || {
              serviceabilityRatio: 0,
              concentrationRisk: 'low' as const,
              lvrRisk: 'low' as const,
              hardshipProbability: 0,
              recommendations: []
            },
            stressTests: debtConfig.stressTests || []
          };
          
          const debtResult = DebtProjectionEngine.calculateDebtRepayments(
            debtConfigWithStrategy,
            year,
            debtBalances
          );
          debtRepayment = debtResult.principal;
          debtInterest = debtResult.interest;
          totalDebtBalance = debtResult.remaining;
          
          // Deduct debt payments from portfolio (principal + interest)
          portfolioValue -= (debtRepayment + debtInterest);
          
          // Update balances for next year
          debtBalances = Object.fromEntries(
            Object.entries(debtResult.byAccount).map(([id, data]) => [id, data.balance])
          );
        } catch (error) {
          logger.warn('⚠️ Error processing debt repayments:', error);
          // Continue simulation without debt processing
        }
      }
      
      // Task 1.1: Add rental income to portfolio
      portfolioValue += inflationAdjustedRentalIncome;
      
      // Task 1.3: Add partner income to portfolio (if still working)
      const partnerRetirementAge = params.partnerRetirementAge || params.retirementAge;
      if (age < partnerRetirementAge) {
        portfolioValue += currentPartnerIncome;
      }
      
      // Task 2.3: Add cashflow adjustment
      portfolioValue += cashflowAdjustment;
      
      // Enhanced tax calculations with per-path accuracy
      if (this.settings.enableEntityTaxOptimization) {
        const taxResult = this.calculateOptimizedTaxes(
          portfolioValue, 
          currentIncome, 
          age,
          totalReturn, // Actual return for this path
          annualContributions // Contributions for tax calculation
        );
        portfolioValue -= taxResult.taxPayable;
        totalTaxesPaid += taxResult.taxPayable;
      }
      
      // Stress events
      if (stressEvents) {
        const stressMultiplier = this.getStressMultiplier(stressEvents, age);
        portfolioValue *= stressMultiplier;
      }
      
      // Track drawdowns
      peakValue = Math.max(peakValue, portfolioValue);
      const currentDrawdown = (peakValue - portfolioValue) / peakValue;
      maxDrawdown = Math.max(maxDrawdown, currentDrawdown);
      
      // Store enhanced timeline point with new fields
      timeline.push({
        year: new Date().getFullYear() + year,
        age,
        portfolioValue,
        contributions: annualContributions,
        withdrawals: 0,
        phase: 'accumulation' as const,
        totalReturn,
        incomeReturn: yearReturns.income,
        capitalReturn: yearReturns.capital,
        regime: yearReturns.regime,
        taxes: totalTaxesPaid,
        employmentIncome: currentIncome,
        platformFees: platformFee,
        // Task 1.2: Add debt tracking
        debtBalance: totalDebtBalance,
        debtRepayment,
        debtInterest,
        // Task 1.1: Add rental income tracking
        rentalIncome: inflationAdjustedRentalIncome,
        // Task 1.3: Add partner income tracking
        partnerIncome: age < (params.partnerRetirementAge || params.retirementAge) ? currentPartnerIncome : 0,
        // Task 2.1: Add superannuation tax tracking
        contributionsTax,
        division293Tax,
        // Task 2.3: Add cashflow adjustment tracking
        cashflowAdjustment
      });
      
      // Capture path point if enabled
      if (path) {
        path.push({
          t: year,
          year: new Date().getFullYear() + year,
          age,
          value: portfolioValue
        });
      }
    }
    
    // Retirement phase with Australian financial integration
    for (let year = 0; year < params.retireYears; year++) {
      const age = params.retirementAge + year;
      const yearReturns = returns[params.accumYears + year];
      
      // Enhanced withdrawal calculations
      let withdrawal = params.targetAnnualIncome;
      
      // Age Pension integration
      let pensionIncome = 0;
      if (this.settings.enableCentrelinkIntegration && age >= 67) {
        pensionIncome = this.calculateAgePension(portfolioValue, withdrawal, age >= 67);
        withdrawal = Math.max(0, withdrawal - pensionIncome);
        totalPensionReceived += pensionIncome;
      }
      
      // Apply withdrawal
      portfolioValue -= withdrawal;
      
      // Apply returns
      const totalReturn = (yearReturns.growth * params.growthAllocation + 
                          yearReturns.defensive * (1 - params.growthAllocation)) / 100;
      portfolioValue *= (1 + totalReturn);
      
      // Apply platform fees using comprehensive calculation
      const platformFee = PortfolioSimulator.calculatePlatformFees(portfolioValue, this.currentScenario!);
      portfolioValue -= platformFee;
      
      // Retirement phase tax treatment with TBC compliance
      if (this.settings.enableEntityTaxOptimization) {
        const taxResult = this.calculateRetirementTaxes(
          portfolioValue, 
          withdrawal + pensionIncome, 
          age,
          totalReturn // Actual return for accurate tax calculation
        );
        portfolioValue -= taxResult.taxPayable;
        totalTaxesPaid += taxResult.taxPayable;
      }
      
      // Stress events
      if (stressEvents) {
        const stressMultiplier = this.getStressMultiplier(stressEvents, age);
        portfolioValue *= stressMultiplier;
      }
      
      // Track drawdowns
      peakValue = Math.max(peakValue, portfolioValue);
      const currentDrawdown = (peakValue - portfolioValue) / peakValue;
      maxDrawdown = Math.max(maxDrawdown, currentDrawdown);
      
      // Store enhanced timeline point
      timeline.push({
        year: new Date().getFullYear() + params.accumYears + year,
        age,
        portfolioValue,
        contributions: 0,
        withdrawals: withdrawal,
        phase: 'retirement' as const,
        totalReturn,
        incomeReturn: yearReturns.income,
        capitalReturn: yearReturns.capital,
        regime: yearReturns.regime,
        pensionIncome,
        taxes: totalTaxesPaid,
        portfolioWithdrawal: withdrawal,
        platformFees: platformFee
      });
      
      // Capture path point if enabled
      if (path) {
        path.push({
          t: params.accumYears + year,
          year: new Date().getFullYear() + params.accumYears + year,
          age,
          value: portfolioValue
        });
      }
    }
    
    // Detect floor breach if path was captured
    let breachedFloor = false;
    let breachedAt: number | undefined;
    const floorThreshold = params.initialPortfolio * 0.5; // Default: 50% of initial portfolio
    
    if (path && floorThreshold) {
      for (const point of path) {
        if (point.value < floorThreshold) {
          breachedFloor = true;
          breachedAt = point.year;
          break;
        }
      }
    }
    
    return {
      finalValue: portfolioValue,
      timeline,
      maxDrawdown,
      // Enhanced metrics
      totalTaxesPaid,
      totalPensionReceived,
      totalSuperContributions,
      regimeSwitches: this.countRegimeSwitches(timeline),
      averageCorrelation: this.calculateAverageCorrelation(returns),
      // Path visualization data
      path,
      breachedFloor,
      breachedAt,
      trialIndex
    };
  }

  /**
   * Optimize superannuation contributions with tax efficiency
   */
  private optimizeSuperContributions(income: number, age: number, baseContributions: number) {
    const concessionalCap = age >= 50 ? 30000 : 27500; // 2024 caps
    const sgContribution = income * 0.115; // Superannuation Guarantee
    
    // Calculate optimal concessional contributions
    const availableConcessionalSpace = Math.max(0, concessionalCap - sgContribution);
    const taxSaving = Math.min(availableConcessionalSpace, baseContributions) * 0.15; // 15% vs marginal rate
    
    return {
      concessionalContributions: Math.min(availableConcessionalSpace, baseContributions),
      nonConcessionalContributions: Math.max(0, baseContributions - availableConcessionalSpace),
      totalContributions: baseContributions,
      taxSaving
    };
  }

  /**
   * Calculate Age Pension with assets and income tests
   */
  private calculateAgePension(assets: number, income: number, eligible: boolean): number {
    if (!eligible) return 0;
    
    const maxPensionSingle = 26567; // 2024 rate
    const assetThresholdSingle = 301750;
    const incomeThresholdSingle = 10608; // Free area
    const assetTaperRate = 0.0375;
    const incomeTaperRate = 0.50;
    
    // Assets test reduction
    const assetReduction = Math.max(0, (assets - assetThresholdSingle) * assetTaperRate);
    const assetTestPension = Math.max(0, maxPensionSingle - assetReduction);
    
    // Income test reduction  
    const incomeReduction = Math.max(0, (income - incomeThresholdSingle) * incomeTaperRate);
    const incomeTestPension = Math.max(0, maxPensionSingle - incomeReduction);
    
    // Return lower of the two tests
    return Math.min(assetTestPension, incomeTestPension);
  }

  /**
   * Calculate optimized taxes for accumulation phase
   * Implements proper superannuation tax treatment:
   * - 15% tax on concessional contributions
   * - 15% tax on investment earnings
   * - 10% CGT on capital gains held >12 months (1/3 discount)
   * - Division 293 tax (additional 15%) if income > $250,000
   */
  private calculateOptimizedTaxes(
    portfolioValue: number, 
    income: number, 
    age: number,
    investmentReturn?: number,
    contributions?: number
  ): {
    taxPayable: number;
    superEarnings: number;
    contributionsTax: number;
    earningsTax: number;
    capitalGainsTax: number;
    division293Tax: number;
  } {
    let taxPayable = 0;
    let contributionsTax = 0;
    let earningsTax = 0;
    let capitalGainsTax = 0;
    let division293Tax = 0;
    
    // 1. Contributions tax (15% on concessional contributions)
    if (contributions && contributions > 0) {
      contributionsTax = contributions * 0.15;
      taxPayable += contributionsTax;
    }
    
    // 2. Investment earnings tax (15% in accumulation phase)
    const estimatedReturn = investmentReturn || 0.07; // Default 7% if not provided
    const superEarnings = portfolioValue * estimatedReturn;
    
    // Split into income and capital gains (roughly 30/70 split)
    const incomeComponent = superEarnings * 0.30;
    const capitalGainsComponent = superEarnings * 0.70;
    
    // Income taxed at 15%
    earningsTax = incomeComponent * 0.15;
    taxPayable += earningsTax;
    
    // Capital gains with discount (10% effective rate for assets held >12 months)
    capitalGainsTax = capitalGainsComponent * 0.10; // 1/3 discount applied
    taxPayable += capitalGainsTax;
    
    // 3. Division 293 tax (additional 15% if income > $250,000)
    const division293Threshold = 250000;
    if (income > division293Threshold && contributions && contributions > 0) {
      const excessIncome = income - division293Threshold;
      const taxableContributions = Math.min(contributions, excessIncome);
      division293Tax = taxableContributions * 0.15;
      taxPayable += division293Tax;
    }
    
    return {
      taxPayable,
      superEarnings,
      contributionsTax,
      earningsTax,
      capitalGainsTax,
      division293Tax
    };
  }

  /**
   * Calculate retirement phase taxes with pension exemptions
   * Implements proper pension phase tax treatment:
   * - 0% tax on investment earnings if age 60+ in pension phase
   * - Transfer Balance Cap compliance ($2M for 2025/26)
   * - Excess above TBC remains in accumulation phase (15% tax)
   * - Automatic phase transition at preservation age
   */
  private calculateRetirementTaxes(
    portfolioValue: number, 
    income: number, 
    age: number,
    investmentReturn?: number
  ): {
    taxPayable: number;
    inPensionPhase: boolean;
    transferBalanceCapExcess: number;
    pensionPhaseBalance: number;
    accumulationPhaseBalance: number;
  } {
    let taxPayable = 0;
    let inPensionPhase = false;
    let transferBalanceCapExcess = 0;
    let pensionPhaseBalance = 0;
    let accumulationPhaseBalance = 0;
    
    // Get current Transfer Balance Cap
    const caps = SuperannuationCaps2025.getSuperannuationCaps();
    const transferBalanceCap = caps.transferBalanceCap; // $2,000,000 for 2025/26
    
    // Preservation age is 60 (for anyone born after 1964)
    const preservationAge = 60;
    
    if (age >= preservationAge) {
      // Eligible for pension phase
      inPensionPhase = true;
      
      // Check Transfer Balance Cap
      if (portfolioValue <= transferBalanceCap) {
        // Entire balance can be in pension phase (0% tax)
        pensionPhaseBalance = portfolioValue;
        accumulationPhaseBalance = 0;
        taxPayable = 0;
      } else {
        // Excess above TBC remains in accumulation (15% tax)
        pensionPhaseBalance = transferBalanceCap;
        accumulationPhaseBalance = portfolioValue - transferBalanceCap;
        transferBalanceCapExcess = accumulationPhaseBalance;
        
        // Tax the accumulation portion at 15%
        const estimatedReturn = investmentReturn || 0.07;
        const accumulationEarnings = accumulationPhaseBalance * estimatedReturn;
        
        // Split into income (30%) and capital gains (70%)
        const incomeComponent = accumulationEarnings * 0.30;
        const capitalGainsComponent = accumulationEarnings * 0.70;
        
        // Tax at super rates
        const earningsTax = incomeComponent * 0.15;
        const capitalGainsTax = capitalGainsComponent * 0.10; // With CGT discount
        
        taxPayable = earningsTax + capitalGainsTax;
      }
    } else {
      // Under preservation age - transition to retirement rules
      // Cannot access unrestricted pension, but can draw limited amounts
      // Earnings still taxed at 15%
      inPensionPhase = false;
      accumulationPhaseBalance = portfolioValue;
      
      const estimatedReturn = investmentReturn || 0.07;
      const superEarnings = portfolioValue * estimatedReturn;
      
      // Split and tax at accumulation rates
      const incomeComponent = superEarnings * 0.30;
      const capitalGainsComponent = superEarnings * 0.70;
      
      taxPayable = (incomeComponent * 0.15) + (capitalGainsComponent * 0.10);
    }
    
    return {
      taxPayable,
      inPensionPhase,
      transferBalanceCapExcess,
      pensionPhaseBalance,
      accumulationPhaseBalance
    };
  }

  /**
   * Count regime switches in timeline
   */
  private countRegimeSwitches(timeline: any[]): number {
    let switches = 0;
    let currentRegime = timeline[0]?.regime;
    
    for (const point of timeline) {
      if (point.regime && point.regime !== currentRegime) {
        switches++;
        currentRegime = point.regime;
      }
    }
    
    return switches;
  }

  /**
   * Calculate average correlation across all years
   */
  private calculateAverageCorrelation(returns: any[]): number {
    const correlations = returns.map(r => r.correlation || 0.2);
    return correlations.reduce((sum, corr) => sum + corr, 0) / correlations.length;
  }

  /**
   * Enhanced statistics calculation with mathematical validation
   */
  private calculateEnhancedStatistics(trials: any[]) {
    const finalValues = trials.map(trial => trial.finalValue);
    const sortedValues = [...finalValues].sort((a, b) => a - b);
    
    // Basic statistics
    const average = finalValues.reduce((sum, val) => sum + val, 0) / finalValues.length;
    const successCount = finalValues.filter(val => val > 0).length;
    const successRate = (successCount / finalValues.length) * 100;
    
    // Enhanced percentiles
    const percentiles = {
      p10: this.calculatePercentile(sortedValues, 10),
      p25: this.calculatePercentile(sortedValues, 25),
      p50: this.calculatePercentile(sortedValues, 50),
      p75: this.calculatePercentile(sortedValues, 75),
      p90: this.calculatePercentile(sortedValues, 90)
    };
    
    // Mathematical validation metrics
    const standardDeviation = Math.sqrt(
      finalValues.reduce((sum, val) => sum + Math.pow(val - average, 2), 0) / finalValues.length
    );
    
    const distributionHealthScore = this.calculateDistributionHealth(finalValues);
    const convergenceAchieved = standardDeviation / Math.sqrt(finalValues.length) < average * 0.01;
    
    // Advanced metrics from enhanced trials
    const regimeSwitches = trials.reduce((sum, trial) => sum + (trial.regimeSwitches || 0), 0);
    const averageCorrelation = trials.reduce((sum, trial) => sum + (trial.averageCorrelation || 0.2), 0) / trials.length;
    const maxDrawdownPeriod = Math.max(...trials.map(trial => trial.maxDrawdown || 0));
    
    return {
      average,
      successRate,
      percentiles,
      standardDeviation,
      convergenceAchieved,
      distributionHealthScore,
      regimeSwitches,
      averageCorrelation,
      maxDrawdownPeriod,
      medianTrial: trials[Math.floor(trials.length / 2)]
    };
  }

  /**
   * Calculate distribution health score
   */
  private calculateDistributionHealth(values: number[]): number {
    // Check for outliers, skewness, and other distribution issues
    const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
    const median = this.calculatePercentile(values, 50);
    const skewness = Math.abs((mean - median) / mean);
    
    // Health score based on skewness and outlier presence
    if (skewness < 0.1) return 1.0; // Excellent
    if (skewness < 0.3) return 0.8; // Good
    if (skewness < 0.5) return 0.6; // Acceptable
    return 0.4; // Poor
  }

  /**
   * Generate enhanced analysis with mathematical insights
   */
  private generateEnhancedAnalysis(statistics: any, params: any): string {
    const analyses = [];
    
    analyses.push(`Advanced Monte Carlo Analysis: ${this.settings.numberOfSimulations} simulations with mathematical enhancements`);
    analyses.push(`Success rate: ${statistics.successRate.toFixed(1)}% (portfolio survives retirement)`);
    
    if (this.settings.enableFatTails) {
      analyses.push(`Fat-tail modeling: Enhanced tail risk assessment using t-distribution (${this.settings.degreesOfFreedom} degrees of freedom)`);
    }
    
    if (this.settings.enableRegimeSwitching) {
      analyses.push(`Regime switching: ${statistics.regimeSwitches} regime changes detected, average correlation ${(statistics.averageCorrelation * 100).toFixed(1)}%`);
    }
    
    if (statistics.distributionHealthScore < 0.7) {
      analyses.push(`Distribution health: Moderate skewness detected (${(statistics.distributionHealthScore * 100).toFixed(0)}% health score)`);
    }
    
    analyses.push(`Mathematical accuracy: ${statistics.convergenceAchieved ? 'Converged' : 'Within tolerance'} with ${(statistics.standardDeviation / 1000).toFixed(0)}K standard error`);
    
    if (this.settings.enableCentrelinkIntegration) {
      analyses.push('Australian integration: Age Pension, Superannuation optimization, and tax efficiency modeling active');
    }
    
    return analyses.join('. ');
  }

  private validateScenario(scenario: ADTScenario): void {
    if (!scenario.basicInformation) {
      throw new Error('Missing basic information in scenario');
    }
    if (!scenario.strategy) {
      throw new Error('Missing strategy in scenario');
    }
  }

  private extractParameters(scenario: ADTScenario) {
    const currentAge = scenario.basicInformation.currentAge;
    const retirementAge = scenario.basicInformation.retirementAge;
    const lifeExpectancy = scenario.lifeExpectancyAssumptions?.expectedEndOfLife || 95;
    
    // Calculate initial assets from scenario
    const superBalance = scenario.assets?.superBalance || 0;
    const liquidInvestments = scenario.assets?.liquidInvestments || 0;
    const cashAtBank = scenario.assets?.cashAtBank || 0;
    
    // Task 1.1: Add property portfolio equity
    let propertyEquity = 0;
    if (this.settings.enablePropertyIntegration && scenario.propertyPortfolio?.enabled) {
      const totalPropertyValue = scenario.propertyPortfolio.properties?.reduce(
        (sum, prop) => sum + (prop.currentValue || 0),
        0
      ) || scenario.propertyPortfolio.totalValue || 0;
      const totalPropertyDebt = scenario.propertyPortfolio.totalDebt || 0;
      propertyEquity = Math.max(0, totalPropertyValue - totalPropertyDebt);
      logger.info(`🏠 Property equity included: $${propertyEquity.toLocaleString()}`);
    }
    
    // Task 1.3: Add partner assets for joint planning
    let partnerAssets = 0;
    if (this.isJointPlanning()) {
      partnerAssets = (scenario.partnerAssets?.superBalance || 0) +
                      (scenario.partnerAssets?.liquidInvestments || 0) +
                      (scenario.partnerAssets?.otherAssets || 0);
      logger.info(`👥 Partner assets included: $${partnerAssets.toLocaleString()}`);
    }
    
    const initialPortfolio = superBalance + liquidInvestments + cashAtBank + propertyEquity + partnerAssets;
    
    // Calculate basic annual expenses
    const annualExpenses = (scenario.incomeExpenses?.housingExpenses || 0) +
                          (scenario.incomeExpenses?.utilitiesExpenses || 0) +
                          (scenario.incomeExpenses?.foodExpenses || 0) +
                          (scenario.incomeExpenses?.transportExpenses || 0) +
                          (scenario.incomeExpenses?.entertainmentExpenses || 0) +
                          (scenario.incomeExpenses?.healthcareExpenses || 0) +
                          (scenario.incomeExpenses?.insuranceExpenses || 0) +
                          (scenario.incomeExpenses?.otherExpenses || 0);
    
    // Calculate primary income
    const primaryIncome = scenario.incomeExpenses?.primaryIncome || 0;
    
    // Task 1.1: Add rental income from property portfolio
    let rentalIncome = 0;
    if (this.settings.enablePropertyIntegration && scenario.propertyPortfolio?.enabled) {
      rentalIncome = scenario.propertyPortfolio.rentalIncome || 0;
    }
    
    // Task 1.3: Add partner income for joint planning
    let partnerIncome = 0;
    if (this.isJointPlanning()) {
      partnerIncome = (scenario.partnerIncome?.primaryIncome || 0) +
                      (scenario.partnerIncome?.secondaryIncome || 0);
    }
    
    // Withdrawal rate from strategy
    const withdrawalRate = (scenario.strategy?.withdrawalRate || 4) / 100;
    
    // Parameter safeguards - Add validation and warnings
    const warnings: string[] = [];
    
    // 1. Minimum portfolio size relative to expenses
    const expenseMultiple = initialPortfolio / (annualExpenses || 50000);
    if (expenseMultiple < 10) {
      warnings.push(`Low portfolio-to-expense ratio (${expenseMultiple.toFixed(1)}x). Consider reducing expenses or increasing assets.`);
    }
    
    // 2. Sustainable withdrawal rate validation (4% rule)
    if (withdrawalRate > 0.06) {
      warnings.push(`High withdrawal rate (${(withdrawalRate * 100).toFixed(1)}%). Standard advice suggests 4% for sustainable retirement.`);
    }
    
    // 3. Income replacement rate warning
    const retirementDrawdown = initialPortfolio * withdrawalRate;
    const replacementRate = retirementDrawdown / (primaryIncome || 50000);
    if (replacementRate < 0.6 && primaryIncome > 50000) {
      warnings.push(`Low income replacement rate (${(replacementRate * 100).toFixed(0)}%). Consider increasing retirement savings.`);
    }
    
    // 4. Time horizon validation
    const accumYears = retirementAge - currentAge;
    const retireYears = lifeExpectancy - retirementAge;
    if (accumYears < 5) {
      warnings.push(`Short accumulation period (${accumYears} years). Consider extending working years or reducing retirement income expectations.`);
    }
    
    // Log warnings for user feedback
    if (warnings.length > 0) {
      logger.warn('⚠️ Parameter Validation Warnings:', warnings);
    }
    
    return {
      currentAge,
      retirementAge,
      lifeExpectancy,
      accumYears,
      retireYears: lifeExpectancy - retirementAge,
      initialPortfolio,
      annualContributions: (scenario.superannuationContributions?.totalContributions || 0) * 12,
      targetAnnualIncome: (scenario.strategy?.replacementRateTarget || 70) * (primaryIncome || 80000) / 100,
      primaryIncome,
      expectedReturn: 0.07,
      defensiveReturn: 0.04,
      volatility: 0.15,
      defensiveVolatility: 0.08,
      growthAllocation: (scenario.strategy?.allocationToGrowth || 70) / 100,
      // Extract all platform fee components from scenario
      platformCostPercentage: scenario.strategy?.platformCostPercentage || 0.0065,
      platformCostFixed: scenario.strategy?.platformCostFixed || 0,
      platformFeeMinAmount: scenario.strategy?.platformFeeMinAmount,
      platformFeeMaxAmount: scenario.strategy?.platformFeeMaxAmount,
      platformFeeMinPercentage: scenario.strategy?.platformFeeMinPercentage,
      platformFeeMaxPercentage: scenario.strategy?.platformFeeMaxPercentage,
      wageGrowthRate: 3.5,
      annualExpenses,
      withdrawalRate,
      inflationRate: 2.5,
      
      // Add rental and partner income to params
      rentalIncome,
      partnerIncome,
      partnerRetirementAge: scenario.basicInformation?.partnerRetirementAge || retirementAge,
      
      // Add safeguard metadata
      validationWarnings: warnings
    };
  }
  
  /**
   * Task 1.3: Check if this is joint planning scenario
   */
  private isJointPlanning(): boolean {
    return !!(
      this.currentScenario?.basicInformation?.hasPartner &&
      this.currentScenario?.basicInformation?.jointFinancialPlanning
    );
  }

  private calculatePercentile(sortedArray: number[], percentile: number): number {
    const index = (percentile / 100) * (sortedArray.length - 1);
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    
    if (lower === upper) return sortedArray[lower];
    
    const weight = index - lower;
    return sortedArray[lower] * (1 - weight) + sortedArray[upper] * weight;
  }

  private seededRandom(seed: number): number {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
  }

  /**
   * Get Australian regulatory minimum pension drawdown rate by age
   * Source: ATO Minimum annual payments for super income streams
   */
  private getMinimumDrawdownRate(age: number): number {
    if (age < 65) return 0.04;      // 4%
    if (age < 75) return 0.05;      // 5%
    if (age < 80) return 0.06;      // 6%
    if (age < 85) return 0.07;      // 7%
    if (age < 90) return 0.09;      // 9%
    if (age < 95) return 0.11;      // 11%
    return 0.14;                     // 14% for 95+
  }

  /**
   * Calculate net rental income (Centrelink assessment basis)
   */
  private calculateRentalIncome(scenario: ADTScenario, yearIndex: number, params: any): number {
    const propertyValue = scenario.assets?.investmentPropertyValue || 0;
    if (propertyValue === 0) return 0;
    
    const grossRent = scenario.incomeExpenses?.rentalIncome || (propertyValue * 0.04);
    const loanBalance = scenario.debts?.investmentLoanBalance || 0;
    const propertyExpenses = 
      (loanBalance * 0.065) +
      (propertyValue * 0.01) +
      (grossRent * 0.07);
    
    const netRent = Math.max(0, grossRent - propertyExpenses);
    const inflationFactor = Math.pow(1 + (params.inflationRate || 2.5) / 100, yearIndex);
    
    return netRent * inflationFactor;
  }

  /**
   * Calculate investment property expenses
   */
  private calculatePropertyExpenses(scenario: ADTScenario, yearIndex: number, params: any): number {
    const propertyValue = scenario.assets?.investmentPropertyValue || 0;
    if (propertyValue === 0) return 0;
    
    const loanBalance = scenario.debts?.investmentLoanBalance || 0;
    const expenses = 
      (propertyValue * 0.01) +
      (propertyValue * 0.0025) +
      (propertyValue * 0.002) +
      (loanBalance * 0.065);
    
    const inflationFactor = Math.pow(1 + (params.inflationRate || 2.5) / 100, yearIndex);
    return expenses * inflationFactor;
  }

  /**
   * Calculate deemed income for Centrelink means testing
   */
  private calculateDeemedIncome(financialAssets: number, age: number): number {
    const LOWER_DEEMING_THRESHOLD_SINGLE = 60400;
    const LOWER_DEEMING_RATE = 0.0025;
    const UPPER_DEEMING_RATE = 0.0225;
    
    if (financialAssets <= LOWER_DEEMING_THRESHOLD_SINGLE) {
      return financialAssets * LOWER_DEEMING_RATE;
    }
    
    const lowerBandIncome = LOWER_DEEMING_THRESHOLD_SINGLE * LOWER_DEEMING_RATE;
    const upperBandAssets = financialAssets - LOWER_DEEMING_THRESHOLD_SINGLE;
    const upperBandIncome = upperBandAssets * UPPER_DEEMING_RATE;
    
    return lowerBandIncome + upperBandIncome;
  }

  private getStressMultiplier(stressEvents: TimelineEvent[], age: number): number {
    let multiplier = 1;
    
    for (const event of stressEvents) {
      if (age >= event.startAge && age <= event.startAge + event.duration) {
        multiplier *= (1 - event.severity / 100);
      }
    }
    
    return multiplier;
  }

  /**
   * Calculate platform fees percentiles across all trials
   * Aggregates the platformFees field from timeline points
   */
  private calculatePlatformFeesPercentiles(trials: any[], params: any, scenario: ADTScenario) {
    const totalYears = params.accumYears + params.retireYears;
    const percentiles: any[] = [];
    
    for (let yearIndex = 0; yearIndex < totalYears; yearIndex++) {
      // Collect fees for this year across all trials
      const yearlyFees = trials
        .map(trial => trial.timeline[yearIndex]?.platformFees || 0)
        .filter(fee => !isNaN(fee) && fee >= 0)
        .sort((a, b) => a - b);
      
      if (yearlyFees.length === 0) continue;
      
      const trialCount = yearlyFees.length;
      const age = params.currentAge + yearIndex;
      const year = new Date().getFullYear() + yearIndex;
      
      percentiles.push({
        year,
        age,
        yearIndex,
        p10: yearlyFees[Math.floor(trialCount * 0.10)],
        p25: yearlyFees[Math.floor(trialCount * 0.25)],
        p50: yearlyFees[Math.floor(trialCount * 0.50)],
        p75: yearlyFees[Math.floor(trialCount * 0.75)],
        p90: yearlyFees[Math.floor(trialCount * 0.90)],
        mean: yearlyFees.reduce((sum, val) => sum + val, 0) / yearlyFees.length,
        min: yearlyFees[0],
        max: yearlyFees[trialCount - 1]
      });
    }
    
    // Calculate cumulative fees
    const cumulativeP10: number[] = [];
    const cumulativeP50: number[] = [];
    const cumulativeP90: number[] = [];
    
    let cumP10 = 0, cumP50 = 0, cumP90 = 0;
    
    percentiles.forEach(p => {
      cumP10 += p.p10;
      cumP50 += p.p50;
      cumP90 += p.p90;
      
      cumulativeP10.push(cumP10);
      cumulativeP50.push(cumP50);
      cumulativeP90.push(cumP90);
    });
    
    const totalFeesP50 = cumP50;
    const startingPortfolio = params.initialPortfolio;
    
    return {
      years: percentiles.map(p => p.year),
      ages: percentiles.map(p => p.age),
      annual: {
        P10: percentiles.map(p => p.p10),
        P25: percentiles.map(p => p.p25),
        P50: percentiles.map(p => p.p50),
        P75: percentiles.map(p => p.p75),
        P90: percentiles.map(p => p.p90)
      },
      cumulative: {
        P10: cumulativeP10,
        P50: cumulativeP50,
        P90: cumulativeP90
      },
      metadata: {
        totalFeesP50,
        feePercentageOfStart: (totalFeesP50 / startingPortfolio) * 100,
        averageAnnualFee: totalFeesP50 / totalYears,
        feeStructure: {
          percentageFee: scenario.strategy?.platformCostPercentage,
          fixedFee: scenario.strategy?.platformCostFixed,
          minAmount: scenario.strategy?.platformFeeMinAmount,
          maxAmount: scenario.strategy?.platformFeeMaxAmount,
          minPercentage: scenario.strategy?.platformFeeMinPercentage,
          maxPercentage: scenario.strategy?.platformFeeMaxPercentage
        }
      }
    };
  }

  /**
   * Calculate income percentiles across all trials
   * Includes employment, superannuation pension, Age Pension, and rental income
   */
  private calculateIncomePercentiles(trials: any[], params: any, scenario: ADTScenario) {
    const totalYears = params.accumYears + params.retireYears;
    
    const employmentByYear: number[][] = Array(totalYears).fill(0).map(() => []);
    const portfolioByYear: number[][] = Array(totalYears).fill(0).map(() => []);
    const pensionByYear: number[][] = Array(totalYears).fill(0).map(() => []);
    const agePensionByYear: number[][] = Array(totalYears).fill(0).map(() => []);
    const rentalByYear: number[][] = Array(totalYears).fill(0).map(() => []);
    const totalIncomeByYear: number[][] = Array(totalYears).fill(0).map(() => []);
    
    trials.forEach((trial, trialIndex) => {
      for (let yearIndex = 0; yearIndex < totalYears; yearIndex++) {
        const age = params.currentAge + yearIndex;
        const isRetired = age >= params.retirementAge;
        const timelinePoint = trial.timeline[yearIndex];
        
        if (!timelinePoint) continue;
        
        const employmentIncome = isRetired ? 0 : (timelinePoint.employmentIncome || params.primaryIncome);
        
        let pensionIncome = 0;
        const superBalance = timelinePoint.portfolioValue || 0;
        
        if (isRetired && superBalance > 0) {
          const minDrawdownRate = this.getMinimumDrawdownRate(age);
          const targetRate = params.withdrawalRate / 100;
          const effectiveRate = Math.max(minDrawdownRate, targetRate);
          pensionIncome = superBalance * effectiveRate;
        }
        
        // Prevent double-counting: during retirement, pensionIncome is calculated from super balance
        // so we set portfolioWithdrawal to 0 to avoid adding the same amount twice
        const portfolioWithdrawal = isRetired ? 0 : (timelinePoint.portfolioWithdrawal || 0);
        const rentalIncome = this.calculateRentalIncome(scenario, yearIndex, params);
        
        let agePension = 0;
        if (age >= 67 && this.settings.enableCentrelinkIntegration) {
          const deemedIncome = this.calculateDeemedIncome(superBalance, age);
          const assessableIncome = rentalIncome + deemedIncome;
          const isHomeowner = (scenario.assets?.ownHomeValue || 0) > 0;
          
          // Extract couple status and partner financials from scenario
          const relationshipStatus = scenario.basicInformation?.relationshipStatus || 'single';
          const isCouple = relationshipStatus === 'married' || relationshipStatus === 'defacto';
          
          // Calculate partner's total assets and income
          const partnerAssets = isCouple ? (
            (scenario.assets?.partnerSuperBalance || 0) +
            (scenario.assets?.partnerLiquidInvestments || 0) +
            (scenario.assets?.partnerCashAtBank || 0) +
            (scenario.assets?.partnerOtherAssets || 0)
          ) : 0;
          
          const partnerIncome = isCouple ? (
            (scenario.incomeExpenses?.partnerPrimaryIncome || 0) +
            (scenario.incomeExpenses?.partnerSecondaryIncome || 0)
          ) : 0;
          
          const pensionResult = AustralianPensionCalculator.calculatePensionEntitlement(
            superBalance,
            assessableIncome,
            isCouple,
            isHomeowner,
            partnerIncome,
            partnerAssets
          );
          
          agePension = pensionResult.reducedPensionAmount;
        }
        
        const totalIncome = employmentIncome + pensionIncome + portfolioWithdrawal + agePension + rentalIncome;
        
        employmentByYear[yearIndex].push(employmentIncome);
        portfolioByYear[yearIndex].push(portfolioWithdrawal);
        pensionByYear[yearIndex].push(pensionIncome);
        agePensionByYear[yearIndex].push(agePension);
        rentalByYear[yearIndex].push(rentalIncome);
        totalIncomeByYear[yearIndex].push(totalIncome);
      }
    });
    
    const years: number[] = [];
    const ages: number[] = [];
    const totalP10: number[] = [];
    const totalP25: number[] = [];
    const totalP50: number[] = [];
    const totalP75: number[] = [];
    const totalP90: number[] = [];
    
    const employmentP10: number[] = [];
    const employmentP50: number[] = [];
    const employmentP90: number[] = [];
    const portfolioP10: number[] = [];
    const portfolioP50: number[] = [];
    const portfolioP90: number[] = [];
    const pensionP10: number[] = [];
    const pensionP50: number[] = [];
    const pensionP90: number[] = [];
    const agePensionP10: number[] = [];
    const agePensionP50: number[] = [];
    const agePensionP90: number[] = [];
    const rentalP10: number[] = [];
    const rentalP50: number[] = [];
    const rentalP90: number[] = [];
    
    const sortAndPercentile = (arr: number[], p: number) => {
      if (arr.length === 0) return 0;
      const sorted = [...arr].sort((a, b) => a - b);
      const index = Math.floor((p / 100) * sorted.length);
      return sorted[Math.min(index, sorted.length - 1)];
    };
    
    for (let yearIndex = 0; yearIndex < totalYears; yearIndex++) {
      const age = params.currentAge + yearIndex;
      const year = new Date().getFullYear() + yearIndex;
      
      years.push(year);
      ages.push(age);
      
      totalP10.push(sortAndPercentile(totalIncomeByYear[yearIndex], 10));
      totalP25.push(sortAndPercentile(totalIncomeByYear[yearIndex], 25));
      totalP50.push(sortAndPercentile(totalIncomeByYear[yearIndex], 50));
      totalP75.push(sortAndPercentile(totalIncomeByYear[yearIndex], 75));
      totalP90.push(sortAndPercentile(totalIncomeByYear[yearIndex], 90));
      
      employmentP10.push(sortAndPercentile(employmentByYear[yearIndex], 10));
      employmentP50.push(sortAndPercentile(employmentByYear[yearIndex], 50));
      employmentP90.push(sortAndPercentile(employmentByYear[yearIndex], 90));
      
      portfolioP10.push(sortAndPercentile(portfolioByYear[yearIndex], 10));
      portfolioP50.push(sortAndPercentile(portfolioByYear[yearIndex], 50));
      portfolioP90.push(sortAndPercentile(portfolioByYear[yearIndex], 90));
      
      pensionP10.push(sortAndPercentile(pensionByYear[yearIndex], 10));
      pensionP50.push(sortAndPercentile(pensionByYear[yearIndex], 50));
      pensionP90.push(sortAndPercentile(pensionByYear[yearIndex], 90));
      
      agePensionP10.push(sortAndPercentile(agePensionByYear[yearIndex], 10));
      agePensionP50.push(sortAndPercentile(agePensionByYear[yearIndex], 50));
      agePensionP90.push(sortAndPercentile(agePensionByYear[yearIndex], 90));
      
      rentalP10.push(sortAndPercentile(rentalByYear[yearIndex], 10));
      rentalP50.push(sortAndPercentile(rentalByYear[yearIndex], 50));
      rentalP90.push(sortAndPercentile(rentalByYear[yearIndex], 90));
    }
    
    const cumulativeP10: number[] = [];
    const cumulativeP50: number[] = [];
    const cumulativeP90: number[] = [];
    
    let cumP10 = 0, cumP50 = 0, cumP90 = 0;
    totalP10.forEach((p10, i) => {
      cumP10 += p10;
      cumP50 += totalP50[i];
      cumP90 += totalP90[i];
      
      cumulativeP10.push(cumP10);
      cumulativeP50.push(cumP50);
      cumulativeP90.push(cumP90);
    });
    
    const totalIncomeP50 = cumP50;
    const avgAnnualIncome = totalIncomeP50 / totalYears;
    const preRetirementIncome = totalP50.slice(0, params.accumYears).reduce((a, b) => a + b, 0);
    const postRetirementIncome = totalP50.slice(params.accumYears).reduce((a, b) => a + b, 0);
    
    // Determine dominant income source from P50 values
    const sourceAverages = {
      employment: employmentP50.reduce((a, b) => a + b, 0) / totalYears,
      pension: pensionP50.reduce((a, b) => a + b, 0) / totalYears,
      agePension: agePensionP50.reduce((a, b) => a + b, 0) / totalYears,
      rental: rentalP50.reduce((a, b) => a + b, 0) / totalYears,
      portfolio: portfolioP50.reduce((a, b) => a + b, 0) / totalYears
    };
    const dominantSource = Object.entries(sourceAverages).reduce((a, b) => b[1] > a[1] ? b : a)[0];
    
    // Diagnostic logging for Phase 2C verification
    console.log('✅ [ENGINE] Income percentiles calculated:', {
      years: years.length,
      structure: 'object',
      hasAnnual: !!totalP50,
      hasSources: true,
      sample: {
        year: years[0],
        age: ages[0],
        P50: totalP50[0]
      }
    });
    
    return {
      years,
      ages,
      annual: {
        P10: totalP10,
        P25: totalP25,
        P50: totalP50,
        P75: totalP75,
        P90: totalP90
      },
      cumulative: {
        P10: cumulativeP10,
        P50: cumulativeP50,
        P90: cumulativeP90
      },
      sources: {
        employment: { P10: employmentP10, P50: employmentP50, P90: employmentP90 },
        portfolio: { P10: portfolioP10, P50: portfolioP50, P90: portfolioP90 },
        pension: { P10: pensionP10, P50: pensionP50, P90: pensionP90 },
        agePension: { P10: agePensionP10, P50: agePensionP50, P90: agePensionP90 },
        rental: { P10: rentalP10, P50: rentalP50, P90: rentalP90 }
      },
      metadata: {
        totalIncomeP50,
        averageAnnualIncome: avgAnnualIncome,
        dominantSource
      }
    };
  }

  /**
   * Calculate expense percentiles across all trials
   * Includes living expenses with inflation, age-based adjustments, and property costs
   */
  private calculateExpensePercentiles(trials: any[], params: any, scenario: ADTScenario) {
    const totalYears = params.accumYears + params.retireYears;
    const inflationRate = params.inflationRate || 2.5;
    
    const baseHousing = scenario.incomeExpenses?.housingExpenses || 0;
    const baseUtilities = scenario.incomeExpenses?.utilitiesExpenses || 0;
    const baseTransport = scenario.incomeExpenses?.transportExpenses || 0;
    const baseFood = scenario.incomeExpenses?.foodExpenses || 0;
    const baseHealthcare = scenario.incomeExpenses?.healthcareExpenses || 0;
    const baseEntertainment = scenario.incomeExpenses?.entertainmentExpenses || 0;
    const baseOther = scenario.incomeExpenses?.otherExpenses || 0;
    
    const isMonthly = scenario.incomeExpenses?.expensesFrequency === 'monthly';
    const multiplier = isMonthly ? 12 : 1;
    
    const housingByYear: number[][] = Array(totalYears).fill(0).map(() => []);
    const utilitiesByYear: number[][] = Array(totalYears).fill(0).map(() => []);
    const transportByYear: number[][] = Array(totalYears).fill(0).map(() => []);
    const foodByYear: number[][] = Array(totalYears).fill(0).map(() => []);
    const healthcareByYear: number[][] = Array(totalYears).fill(0).map(() => []);
    const entertainmentByYear: number[][] = Array(totalYears).fill(0).map(() => []);
    const propertyByYear: number[][] = Array(totalYears).fill(0).map(() => []);
    const otherByYear: number[][] = Array(totalYears).fill(0).map(() => []);
    const totalByYear: number[][] = Array(totalYears).fill(0).map(() => []);
    
    trials.forEach((trial, trialIndex) => {
      const seed = this.randomSeed ? this.randomSeed + trialIndex : trialIndex;
      const variationFactor = 0.90 + (this.seededRandom(seed) * 0.20);
      
      for (let yearIndex = 0; yearIndex < totalYears; yearIndex++) {
        const age = params.currentAge + yearIndex;
        const inflationFactor = Math.pow(1 + inflationRate / 100, yearIndex);
        
        let healthcareMultiplier = 1.0;
        let transportMultiplier = 1.0;
        let entertainmentMultiplier = 1.0;
        
        // Check age >= 85 first to ensure correct multipliers for 85+
        if (age >= 85) {
          healthcareMultiplier = 2.0;
          transportMultiplier = 0.6;
          entertainmentMultiplier = 0.7;
        } else if (age >= 75) {
          healthcareMultiplier = 1.5;
          transportMultiplier = 0.8;
          entertainmentMultiplier = 0.9;
        }
        
        const housing = baseHousing * multiplier * inflationFactor * variationFactor;
        const utilities = baseUtilities * multiplier * inflationFactor * variationFactor;
        const transport = baseTransport * multiplier * inflationFactor * transportMultiplier * variationFactor;
        const food = baseFood * multiplier * inflationFactor * variationFactor;
        const healthcare = baseHealthcare * multiplier * inflationFactor * healthcareMultiplier * variationFactor;
        const entertainment = baseEntertainment * multiplier * inflationFactor * entertainmentMultiplier * variationFactor;
        const other = baseOther * multiplier * inflationFactor * variationFactor;
        const property = this.calculatePropertyExpenses(scenario, yearIndex, params);
        
        const total = housing + utilities + transport + food + healthcare + entertainment + property + other;
        
        housingByYear[yearIndex].push(housing);
        utilitiesByYear[yearIndex].push(utilities);
        transportByYear[yearIndex].push(transport);
        foodByYear[yearIndex].push(food);
        healthcareByYear[yearIndex].push(healthcare);
        entertainmentByYear[yearIndex].push(entertainment);
        propertyByYear[yearIndex].push(property);
        otherByYear[yearIndex].push(other);
        totalByYear[yearIndex].push(total);
      }
    });
    
    const years: number[] = [];
    const ages: number[] = [];
    const totalP10: number[] = [];
    const totalP25: number[] = [];
    const totalP50: number[] = [];
    const totalP75: number[] = [];
    const totalP90: number[] = [];
    
    const housingP50: number[] = [];
    const utilitiesP50: number[] = [];
    const transportP50: number[] = [];
    const foodP50: number[] = [];
    const healthcareP50: number[] = [];
    const personalP50: number[] = [];
    const entertainmentP50: number[] = [];
    const travelP50: number[] = [];
    const propertyP50: number[] = [];
    const otherP50: number[] = [];
    
    const sortAndPercentile = (arr: number[], p: number) => {
      if (arr.length === 0) return 0;
      const sorted = [...arr].sort((a, b) => a - b);
      const index = Math.floor((p / 100) * sorted.length);
      return sorted[Math.min(index, sorted.length - 1)];
    };
    
    for (let yearIndex = 0; yearIndex < totalYears; yearIndex++) {
      const age = params.currentAge + yearIndex;
      const year = new Date().getFullYear() + yearIndex;
      
      years.push(year);
      ages.push(age);
      
      totalP10.push(sortAndPercentile(totalByYear[yearIndex], 10));
      totalP25.push(sortAndPercentile(totalByYear[yearIndex], 25));
      totalP50.push(sortAndPercentile(totalByYear[yearIndex], 50));
      totalP75.push(sortAndPercentile(totalByYear[yearIndex], 75));
      totalP90.push(sortAndPercentile(totalByYear[yearIndex], 90));
      
      housingP50.push(sortAndPercentile(housingByYear[yearIndex], 50));
      utilitiesP50.push(sortAndPercentile(utilitiesByYear[yearIndex], 50));
      transportP50.push(sortAndPercentile(transportByYear[yearIndex], 50));
      foodP50.push(sortAndPercentile(foodByYear[yearIndex], 50));
      healthcareP50.push(sortAndPercentile(healthcareByYear[yearIndex], 50));
      personalP50.push(0); // Not tracked separately, included in other
      entertainmentP50.push(sortAndPercentile(entertainmentByYear[yearIndex], 50));
      travelP50.push(0); // Not tracked separately, included in entertainment/other
      propertyP50.push(sortAndPercentile(propertyByYear[yearIndex], 50));
      otherP50.push(sortAndPercentile(otherByYear[yearIndex], 50));
    }
    
    const cumulativeP10: number[] = [];
    const cumulativeP50: number[] = [];
    const cumulativeP90: number[] = [];
    
    let cumP10 = 0, cumP50 = 0, cumP90 = 0;
    totalP10.forEach((p10, i) => {
      cumP10 += p10;
      cumP50 += totalP50[i];
      cumP90 += totalP90[i];
      
      cumulativeP10.push(cumP10);
      cumulativeP50.push(cumP50);
      cumulativeP90.push(cumP90);
    });
    
    const totalExpensesP50 = cumP50;
    const avgAnnualExpense = totalExpensesP50 / totalYears;
    
    // Determine dominant expense category
    const categoryAverages = {
      housing: housingP50.reduce((a, b) => a + b, 0) / totalYears,
      utilities: utilitiesP50.reduce((a, b) => a + b, 0) / totalYears,
      transport: transportP50.reduce((a, b) => a + b, 0) / totalYears,
      groceries: foodP50.reduce((a, b) => a + b, 0) / totalYears,
      healthcare: healthcareP50.reduce((a, b) => a + b, 0) / totalYears,
      entertainment: entertainmentP50.reduce((a, b) => a + b, 0) / totalYears,
      property: propertyP50.reduce((a, b) => a + b, 0) / totalYears,
      other: otherP50.reduce((a, b) => a + b, 0) / totalYears
    };
    const dominantCategory = Object.entries(categoryAverages).reduce((a, b) => b[1] > a[1] ? b : a)[0];
    
    // Diagnostic logging for Phase 2C verification
    console.log('✅ [ENGINE] Expense percentiles calculated:', {
      years: years.length,
      structure: 'object',
      hasAnnual: !!totalP50,
      hasCategories: true,
      sample: {
        year: years[0],
        age: ages[0],
        P50: totalP50[0]
      }
    });
    
    return {
      years,
      ages,
      annual: {
        P10: totalP10,
        P25: totalP25,
        P50: totalP50,
        P75: totalP75,
        P90: totalP90
      },
      cumulative: {
        P10: cumulativeP10,
        P50: cumulativeP50,
        P90: cumulativeP90
      },
      categories: {
        housing: housingP50,
        utilities: utilitiesP50,
        transport: transportP50,
        groceries: foodP50,
        healthcare: healthcareP50,
        personal: personalP50,
        entertainment: entertainmentP50,
        travel: travelP50,
        property: propertyP50,
        other: otherP50
      },
      metadata: {
        totalExpensesP50,
        averageAnnualExpense: avgAnnualExpense,
        inflationAdjusted: true,
        dominantCategory
      }
    };
  }

  private calculateTimelinePercentiles(trials: any[], params: any) {
    const totalYears = params.accumYears + params.retireYears;
    const percentileData = [];
    
    // Log for debugging
    if (process.env.NODE_ENV === 'development') {
      const maxYears = Math.max(...trials.map(t => t.timeline?.length || 0));
      console.log('📊 [ENGINE] Calculating timeline percentiles:', {
        totalYears,
        maxTrialLength: maxYears,
        trialsCount: trials.length
      });
    }
    
    // Calculate percentiles for ALL years (using totalYears)
    for (let year = 0; year < totalYears; year++) {
      const yearValues = trials
        .map(t => t.timeline?.[year]?.portfolioValue)
        .filter(v => v !== undefined && v !== null && !isNaN(v))
        .sort((a, b) => a - b);
      
      if (yearValues.length > 0) {
        percentileData.push({
          year: new Date().getFullYear() + year,
          age: params.currentAge + year,
          p5: this.calculatePercentile(yearValues, 5),
          p10: this.calculatePercentile(yearValues, 10),
          p25: this.calculatePercentile(yearValues, 25),
          p50: this.calculatePercentile(yearValues, 50),
          p75: this.calculatePercentile(yearValues, 75),
          p90: this.calculatePercentile(yearValues, 90),
          p95: this.calculatePercentile(yearValues, 95),
          mean: yearValues.reduce((sum, val) => sum + val, 0) / yearValues.length
        });
      } else {
        console.warn(`📊 [ENGINE] No valid portfolio values for year ${year}, using zeros`);
        percentileData.push({
          year: new Date().getFullYear() + year,
          age: params.currentAge + year,
          p5: 0,
          p10: 0,
          p25: 0,
          p50: 0,
          p75: 0,
          p90: 0,
          p95: 0,
          mean: 0
        });
      }
    }
    
    // Log result (array format for backward compatibility)
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ [ENGINE] Portfolio percentiles (${percentileData.length} points, array format for legacy compatibility)`);
    }
    
    return percentileData;
  }

  private generateTimelineData(params: any, medianTrial: any) {
    return medianTrial?.timeline || [];
  }

  private generateChartData(timelineData: any[]) {
    return timelineData.map(point => ({
      year: point.year,
      value: point.portfolioValue,
      age: point.age
    }));
  }

  private generateScenarioSample(trials: any[]) {
    // Return sample scenarios for visualization
    const sampleSize = Math.min(10, trials.length);
    const step = Math.floor(trials.length / sampleSize);
    
    return trials.filter((_, index) => index % step === 0).slice(0, sampleSize);
  }

  private generateRecommendation(statistics: any, params: any): string {
    if (statistics.successRate >= 90) {
      return 'Your retirement strategy appears robust with high probability of success.';
    } else if (statistics.successRate >= 70) {
      return 'Consider increasing contributions or adjusting retirement expectations for better outcomes.';
    } else {
      return 'Significant strategy adjustments recommended - consider professional financial advice.';
    }
  }

  /**
   * Foundation tests for mathematical validation
   */
  runFoundationTests() {
    const tests = {
      mathematicalIntegrity: this.testMathematicalIntegrity(),
      correlationStability: this.testCorrelationStability(),
      convergenceValidation: this.testConvergenceValidation(),
      distributionAccuracy: this.testDistributionAccuracy()
    };
    
    logger.info('🧪 Enhanced mathematical foundation tests completed', tests);
    return tests;
  }

  private testMathematicalIntegrity(): boolean {
    // Test fat-tail modeling accuracy
    if (this.settings.enableFatTails) {
      const testReturns = this.generateAdvancedReturns(1000, { expectedReturn: 0.07, volatility: 0.15 }, 12345);
      const kurtosis = this.calculateKurtosis(testReturns.map(r => r.growth));
      return kurtosis > 3; // Should show excess kurtosis for fat tails
    }
    return true;
  }

  private testCorrelationStability(): boolean {
    // Test that correlations behave as expected across regimes
    const testReturns = this.generateAdvancedReturns(1000, { expectedReturn: 0.07, volatility: 0.15 }, 54321);
    const correlations = testReturns.map(r => r.correlation);
    const avgCorrelation = correlations.reduce((sum, c) => sum + c, 0) / correlations.length;
    return avgCorrelation >= 0 && avgCorrelation <= 1; // Valid correlation range
  }

  private testConvergenceValidation(): boolean {
    // Test simulation convergence
    return this.settings.numberOfSimulations >= 1000; // Minimum for reliable results
  }

  private testDistributionAccuracy(): boolean {
    // Test that generated distributions have expected properties
    return true; // Placeholder - would implement full statistical tests
  }

  private calculateKurtosis(values: number[]): number {
    const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
    const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
    const fourthMoment = values.reduce((sum, val) => sum + Math.pow(val - mean, 4), 0) / values.length;
    
    return fourthMoment / Math.pow(variance, 2);
  }

  /**
   * Aggregate superannuation tax data from all trials
   */
  private aggregateSuperannuationTaxData(
    trials: any[],
    startAge: number,
    retirementAge: number
  ): any {
    logger.info('📊 Aggregating superannuation tax data from', trials.length, 'trials');
    
    let totalAccumEarningsTax = 0;
    let totalAccumCGT = 0;
    let totalAccumDiv293 = 0;
    let totalPensionTax = 0;
    let totalTBCExcess = 0;
    let accumYearsCount = 0;
    let pensionYearsCount = 0;
    let totalGrowth = 0;
    let totalInitialValue = 0;
    
    const numTrials = trials.length;
    
    trials.forEach(trial => {
      if (!trial.timeline) return;
      
      trial.timeline.forEach((point: any) => {
        const age = point.age || startAge;
        
        if (age < retirementAge) {
          // Accumulation phase
          totalAccumEarningsTax += point.earningsTax || 0;
          totalAccumCGT += point.capitalGainsTax || 0;
          totalAccumDiv293 += point.division293Tax || 0;
          accumYearsCount++;
        } else {
          // Pension phase
          totalPensionTax += point.pensionTax || 0;
          totalTBCExcess += point.tbcExcessTax || 0;
          pensionYearsCount++;
        }
        
        // Track growth for percentage calculation
        if (point.totalReturn) {
          totalGrowth += point.totalReturn;
        }
        
        if (point.portfolioValue && !totalInitialValue) {
          totalInitialValue = point.portfolioValue;
        }
      });
    });
    
    // Calculate averages
    const avgAccumEarningsTax = totalAccumEarningsTax / numTrials;
    const avgAccumCGT = totalAccumCGT / numTrials;
    const avgAccumDiv293 = totalAccumDiv293 / numTrials;
    const avgPensionTax = totalPensionTax / numTrials;
    const avgTBCExcess = totalTBCExcess / numTrials;
    
    const totalTax = avgAccumEarningsTax + avgAccumCGT + avgAccumDiv293 + avgPensionTax + avgTBCExcess;
    const avgGrowth = totalGrowth / numTrials;
    
    // Calculate TBC utilization (placeholder - would need actual TBC tracking)
    const estimatedTBCUtilization = Math.min(100, (totalInitialValue / 2000000) * 100);
    
    const accumPhaseYears = Math.round(accumYearsCount / numTrials);
    const pensionPhaseYears = Math.round(pensionYearsCount / numTrials);
    
    const taxAsPercentageOfGrowth = avgGrowth > 0 ? (totalTax / avgGrowth) * 100 : 0;
    const averageEffectiveRate = avgGrowth > 0 ? (totalTax / avgGrowth) * 100 : 0;
    
    logger.info('✅ Tax aggregation complete:', {
      totalTax: totalTax.toFixed(2),
      accumPhase: (avgAccumEarningsTax + avgAccumCGT + avgAccumDiv293).toFixed(2),
      pensionPhase: (avgPensionTax + avgTBCExcess).toFixed(2),
      effectiveRate: averageEffectiveRate.toFixed(2) + '%'
    });
    
    return {
      accumulationPhase: {
        earningsTax: avgAccumEarningsTax,
        capitalGainsTax: avgAccumCGT,
        division293Tax: avgAccumDiv293,
        totalTax: avgAccumEarningsTax + avgAccumCGT + avgAccumDiv293,
        years: accumPhaseYears
      },
      pensionPhase: {
        earningsTax: 0, // Always 0 in pension phase (age 60+)
        tbcUtilization: estimatedTBCUtilization,
        tbcExcessTax: avgTBCExcess,
        years: pensionPhaseYears
      },
      totalTaxPaid: totalTax,
      taxAsPercentageOfGrowth,
      transitionAge: retirementAge,
      averageEffectiveRate
    };
  }

  /**
   * Run Kolmogorov-Smirnov test for normality
   */
  private runKolmogorovSmirnovTest(values: number[]): { statistic: number; pValue: number; passed: boolean } {
    const sorted = [...values].sort((a, b) => a - b);
    const n = sorted.length;
    
    // Calculate mean and std dev for normal distribution
    const mean = sorted.reduce((sum, v) => sum + v, 0) / n;
    const variance = sorted.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / n;
    const stdDev = Math.sqrt(variance);
    
    // Calculate KS statistic
    let maxD = 0;
    for (let i = 0; i < n; i++) {
      const empiricalCDF = (i + 1) / n;
      const theoreticalCDF = this.normalCDF((sorted[i] - mean) / stdDev);
      const d = Math.abs(empiricalCDF - theoreticalCDF);
      maxD = Math.max(maxD, d);
    }
    
    // Approximate p-value using Kolmogorov distribution
    const pValue = this.kolmogorovPValue(maxD, n);
    
    return {
      statistic: maxD,
      pValue,
      passed: pValue > 0.05
    };
  }

  /**
   * Run Anderson-Darling test for normality
   */
  private runAndersonDarlingTest(values: number[]): { statistic: number; pValue: number; passed: boolean } {
    const sorted = [...values].sort((a, b) => a - b);
    const n = sorted.length;
    
    // Calculate mean and std dev
    const mean = sorted.reduce((sum, v) => sum + v, 0) / n;
    const variance = sorted.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / n;
    const stdDev = Math.sqrt(variance);
    
    // Calculate A² statistic
    let sum = 0;
    for (let i = 0; i < n; i++) {
      const z = (sorted[i] - mean) / stdDev;
      const phi = this.normalCDF(z);
      const phi_complement = 1 - this.normalCDF((sorted[n - 1 - i] - mean) / stdDev);
      
      if (phi > 0 && phi < 1 && phi_complement > 0 && phi_complement < 1) {
        sum += (2 * i + 1) * (Math.log(phi) + Math.log(phi_complement));
      }
    }
    
    const A2 = -n - sum / n;
    
    // Approximate p-value
    let pValue: number;
    if (A2 < 0.2) pValue = 1 - Math.exp(-13.436 + 101.14 * A2 - 223.73 * A2 * A2);
    else if (A2 < 0.34) pValue = 1 - Math.exp(-8.318 + 42.796 * A2 - 59.938 * A2 * A2);
    else if (A2 < 0.6) pValue = Math.exp(0.9177 - 4.279 * A2 - 1.38 * A2 * A2);
    else pValue = Math.exp(1.2937 - 5.709 * A2 + 0.0186 * A2 * A2);
    
    return {
      statistic: A2,
      pValue,
      passed: pValue > 0.05
    };
  }

  /**
   * Normal cumulative distribution function
   */
  private normalCDF(z: number): number {
    return 0.5 * (1 + this.erf(z / Math.sqrt(2)));
  }

  /**
   * Error function approximation
   */
  private erf(x: number): number {
    const sign = x >= 0 ? 1 : -1;
    x = Math.abs(x);
    
    const a1 = 0.254829592;
    const a2 = -0.284496736;
    const a3 = 1.421413741;
    const a4 = -1.453152027;
    const a5 = 1.061405429;
    const p = 0.3275911;
    
    const t = 1 / (1 + p * x);
    const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
    
    return sign * y;
  }

  /**
   * Calculate p-value for Kolmogorov-Smirnov statistic
   */
  private kolmogorovPValue(D: number, n: number): number {
    const lambda = (Math.sqrt(n) + 0.12 + 0.11 / Math.sqrt(n)) * D;
    
    let sum = 0;
    for (let k = 1; k <= 10; k++) {
      sum += Math.pow(-1, k - 1) * Math.exp(-2 * k * k * lambda * lambda);
    }
    
    return Math.min(1, Math.max(0, 2 * sum));
  }

  /**
   * Determine sampling quality based on sample size and convergence
   */
  private determineSamplingQuality(sampleSize: number, statistics: any): 'excellent' | 'good' | 'adequate' | 'poor' {
    if (sampleSize >= 10000 && statistics.convergenceAchieved) return 'excellent';
    if (sampleSize >= 5000) return 'good';
    if (sampleSize >= 1000) return 'adequate';
    return 'poor';
  }

  /**
   * Generate validation warnings based on test results
   */
  private generateValidationWarnings(ksTest: any, adTest: any, sampleSize: number): string[] {
    const warnings: string[] = [];
    
    if (!ksTest.passed) {
      warnings.push('Kolmogorov-Smirnov test indicates deviation from normal distribution');
    }
    
    if (!adTest.passed) {
      warnings.push('Anderson-Darling test detected non-normality in tails');
    }
    
    if (sampleSize < 1000) {
      warnings.push('Sample size below recommended minimum of 1,000 simulations');
    }
    
    if (ksTest.pValue < 0.01 || adTest.pValue < 0.01) {
      warnings.push('Strong statistical evidence of distribution mismatch');
    }
    
    return warnings;
  }

  /**
   * Get calculation statistics for monitoring
   */
  getCalculationStats() {
    return {
      engineType: 'IntegratedMathematicalEngine',
      version: '3.0',
      capabilities: {
        fatTailModeling: this.settings.enableFatTails,
        regimeSwitching: this.settings.enableRegimeSwitching,
        blackSwanEvents: this.settings.enableBlackSwanEvents,
        australianFinancialIntegration: true,
        incomeCapitalSplit: this.settings.enableIncomeCapitalSplit,
        correlationModeling: this.settings.enableRealisticCorrelations
      },
      settings: this.settings,
      lastExecution: new Date().toISOString()
    };
  }
}
